<script setup>
import { ref, onMounted, computed, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useAppToast } from '@/shared/lib/useAppToast'

import Card from 'primevue/card'
import Button from 'primevue/button'
import InputText from 'primevue/inputtext'
import ToggleButton from 'primevue/togglebutton'
import ConfirmDialog from 'primevue/confirmdialog'

import GetAllUsers from "@/modules/admin/users/components/GetAllUsers.vue";
import UserDetailDialog from "@/modules/admin/users/components/UserDetailDialog.vue";
import RefreshUserPermissionsDialog from "@/modules/admin/users/components/RefreshUserPermissionsDialog.vue";
import ChangeUserRoleDialog from "@/modules/admin/users/components/ChangeUserRoleDialog.vue";

import {
  getAllUsers,
  getUserDetails,
  toggleUserBlock,
  refreshUserPermissions,
  deleteUser,
  restoreUser,
  changeUserRole,
} from "@/modules/admin/users/api/users.service";
import { getPermissions } from "@/modules/auth/api/user.service";

const router = useRouter()
const toast = useAppToast()

const users = ref([])
const visible = ref(false)
const refreshDialogVisible = ref(false)
const showDeleted = ref(false)

const selectedUser = ref(null)
const roles = ref([])
const permissions = ref([])

const search = ref('')
const loading = ref(false)
const detailsLoading = ref(false)
const togglingUserId = ref(null)
const refreshingPermissions = ref(false)

const currentUser = ref(null);
const canEditCompanyDetails = ref(false);

const changeRoleDialogVisible = ref(false)
const selectedRoleUser = ref(null)
const changingRole = ref(false)
const changeRoleError = ref('')
const canChangeUserRole = ref(false)

function getCurrentUser() {
  const userStr = localStorage.getItem('user');
  if (userStr) {
    try {
      return JSON.parse(userStr);
    } catch (e) {
      console.error('Error parsing user', e);
    }
  }
  
  return { 
    id: 1, 
    email: 'admin@offercraft.com',
    roles: ['God'] 
  };
}

function isSuperAdmin(user) {
  return user?.roles?.includes('God') || user?.email === 'admin@offercraft.com';
}

function isCurrentUserSuperAdmin() {
  return currentUser.value && isSuperAdmin(currentUser.value);
}

function canManageUser(user) {
  if (isSuperAdmin(user)) {
    return false;
  }
  
  if (!isCurrentUserSuperAdmin() && user?.roles?.includes('Admin')) {
    return false;
  }
  
  return true;
}

async function loadUsers() {
  try {
    loading.value = true
    users.value = await getAllUsers(showDeleted.value)
    console.log('Loaded users:', users.value.length, 'showDeleted:', showDeleted.value)
  } catch (error) {
    users.value = []
    toast.error(error.message || 'Ошибка при загрузке пользователей')
  } finally {
    loading.value = false
  }
}

watch(showDeleted, () => {
  console.log('Show deleted changed to:', showDeleted.value)
  loadUsers()
})

async function showUser(user) {
  try {
    detailsLoading.value = true

    const response = await getUserDetails(user.id)

    selectedUser.value = response
    roles.value = response.roles ?? []
    permissions.value = response.permissions ?? []
    visible.value = true
  } catch (error) {
    toast.error(error.message || 'Ошибка при получении пользователя')
  } finally {
    detailsLoading.value = false
  }
}

async function toggleBlock(user) {
  if (!canManageUser(user)) {
    toast.warn('Вы не можете изменить статус этого пользователя')
    return;
  }
  
  try {
    togglingUserId.value = user.id

    await toggleUserBlock(user.id)
    user.isActive = !user.isActive

    if (selectedUser.value && selectedUser.value.id === user.id) {
      selectedUser.value.isActive = user.isActive
    }

    toast.success(user.isActive ? 'Пользователь разблокирован' : 'Пользователь заблокирован')
  } catch (error) {
    toast.error(error.message || 'Ошибка при блокировке пользователя')
  } finally {
    togglingUserId.value = null
  }
}

const deletingUser = ref(false)

async function deleteUserHandler(user) {
  if (deletingUser.value) return
  if (!canManageUser(user)) {
    toast.warn('Вы не можете удалить этого пользователя')
    return;
  }
  
  try {
    deletingUser.value = true
    await deleteUser(user.id)
    await loadUsers()

    if (selectedUser.value?.id === user.id) {
      visible.value = false
    }

    toast.success(`Пользователь "${user.email || user.fullName || user.id}" удален`)
  } catch (error) {
    toast.error(error.message || 'Не удалось удалить пользователя')
  } finally {
    deletingUser.value = false
  }
}

async function restoreUserHandler(user) {
  try {
    await restoreUser(user.id)
    await loadUsers()

    if (selectedUser.value?.id === user.id) {
      visible.value = false
    }

    toast.success(`Пользователь "${user.email || user.fullName || user.id}" восстановлен`)
  } catch (error) {
    toast.error(error.message || 'Не удалось восстановить пользователя')
  }
}

async function handleRefreshUserPermissions(userId) {
  try {
    refreshingPermissions.value = true

    await refreshUserPermissions(userId)
    refreshDialogVisible.value = false

    toast.success('Разрешения пользователя обновлены')
  } catch (error) {
    toast.error(error.message || 'Ошибка при обновлении разрешений пользователя')
  } finally {
    refreshingPermissions.value = false
  }
}

function closeDialog() {
  visible.value = false
  selectedUser.value = null
  roles.value = []
  permissions.value = []
}

function closeRefreshDialog() {
  if (refreshingPermissions.value) return
  refreshDialogVisible.value = false
}

function goBack() {
  router.push('/admin')
}

function goToUserDetails(user) {
  if (!canManageUser(user) && !isCurrentUserSuperAdmin()) {
    toast.error('Вы не можете просматривать детали этого пользователя')
    return
  }

  router.push(`/admin/users/${user.id}`)
}

function openChangeRoleDialog(user) {
  selectedRoleUser.value = user
  changeRoleError.value = ''
  changeRoleDialogVisible.value = true
}

function closeChangeRoleDialog() {
  if (changingRole.value) return

  changeRoleDialogVisible.value = false
  selectedRoleUser.value = null
  changeRoleError.value = ''
}

async function handleChangeUserRole(roleName) {
  if (!selectedRoleUser.value) return

  changeRoleError.value = ''

  try {
    changingRole.value = true

    await changeUserRole(selectedRoleUser.value.id, roleName)

    toast.success('Роль пользователя изменена')
    closeChangeRoleDialog()
    await loadUsers()
  } catch (error) {
    changeRoleError.value = error.message || 'Не удалось изменить роль пользователя'
  } finally {
    changingRole.value = false
  }
}

const filteredUsers = computed(() => {
  const searchText = search.value.trim().toLowerCase()

  if (!searchText) {
    return users.value
  }

  return users.value.filter((user) =>
    String(user.email ?? '')
      .toLowerCase()
      .includes(searchText),
  )
})

onMounted(async () => {
  currentUser.value = getCurrentUser()
  loadUsers()

  try {
    const perms = await getPermissions()
    const permList = perms.data ?? []
    canEditCompanyDetails.value = permList.includes('EditCompanyDetails')
    canChangeUserRole.value = permList.includes('ChangeUserRole')
  } catch {
    canEditCompanyDetails.value = false
    canChangeUserRole.value = false 
  }
})

</script>

<template>
  <div class="admin-users-page">
    <div class="page-shell">
      <div class="top-bar">
        <Button
          label="Назад"
          icon="pi pi-arrow-left"
          class="back-btn"
          outlined
          @click="goBack"
        />

        <div class="top-bar-right">
        <div v-if="isCurrentUserSuperAdmin()" class="show-deleted-toggle">
            <ToggleButton
              v-model="showDeleted"
              onLabel="Показывать удаленных"
              offLabel="Скрыть удаленных"
              onIcon="pi pi-eye"
              offIcon="pi pi-eye-slash"
              class="toggle-btn"
            />
          </div>

          <Button
            v-if="isCurrentUserSuperAdmin()"
            label="Обновить права пользователя"
            icon="pi pi-refresh"
            class="refresh-btn"
            :loading="refreshingPermissions"
            :disabled="refreshingPermissions"
            @click="refreshDialogVisible = true"
          />
        </div>
      </div>

      <div class="page-header">
        <div>
          <h1>Пользователи</h1>
          <p>Управление аккаунтами, просмотр деталей и изменение статуса</p>
        </div>
      </div>

      <Card class="users-card">
        <template #content>
          <div class="filters">
            <div class="filter-group filter-group--search">
              <label>Поиск</label>
              <InputText
                v-model="search"
                placeholder="Поиск по email"
                class="filter-control"
              />
            </div>
          </div>

          <div v-if="loading" class="state-block">Загрузка...</div>

          <div v-else-if="filteredUsers.length === 0" class="state-block">
            {{
              showDeleted
                ? "Удаленные пользователи не найдены"
                : "Пользователи не найдены"
            }}
          </div>

          <GetAllUsers
          v-else
          :users="filteredUsers"
          :show-deleted="showDeleted"
          :toggling-user-id="togglingUserId"
          :current-user-id="currentUser?.id"
          :is-super-admin="isCurrentUserSuperAdmin()"
          :can-change-user-role="canChangeUserRole"
          @show-user="showUser"
          @toggle-block="toggleBlock"
          @open-user-page="goToUserDetails"
          @delete-user="deleteUserHandler"
          @restore-user="restoreUserHandler"
          @change-role="openChangeRoleDialog"
          />
        </template>
      </Card>

      <UserDetailDialog
        :visible="visible"
        :selected-user="selectedUser"
        :roles="roles"
        :permissions="permissions"
        :loading="detailsLoading"
        :can-edit-company-details="canEditCompanyDetails"
        @close="closeDialog"
        @updated="(patch) => { if (selectedUser) Object.assign(selectedUser, patch) }"
      />

      <RefreshUserPermissionsDialog
        v-if="isCurrentUserSuperAdmin()"
        :visible="refreshDialogVisible"
        :users="users"
        :submitting="refreshingPermissions"
        @close="closeRefreshDialog"
        @submit="handleRefreshUserPermissions"
      />

      <ChangeUserRoleDialog
        :visible="changeRoleDialogVisible"
        :user="selectedRoleUser"
        :submitting="changingRole"
        :error="changeRoleError"
        @close="closeChangeRoleDialog"
        @submit="handleChangeUserRole"
        @clear-error="changeRoleError = ''"
      />
    </div>

    <ConfirmDialog />
  </div>
</template>

<style scoped>
.admin-users-page {
  background: #fafafa;
  min-height: 100vh;
}

.page-shell {
  max-width: 1280px;
  margin: 0 auto;
  padding: 32px 24px 48px;
}

.top-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-bottom: 24px;
}

.page-header {
  margin-bottom: 24px;
}

.page-header h1 {
  margin: 0;
  font-size: 36px;
  line-height: 1.1;
  letter-spacing: -0.03em;
  color: #111111;
}

.page-header p {
  margin: 10px 0 0;
  font-size: 15px;
  color: #6b6b6b;
}

.users-card {
  border: 1px solid #ececec;
  border-radius: 24px;
  box-shadow: 0 18px 40px rgba(17, 17, 17, 0.05);
  overflow: hidden;
}

:deep(.users-card.p-card) {
  background: #ffffff;
}

:deep(.users-card .p-card-content) {
  padding: 0;
}

.filters {
  padding: 24px;
  border-bottom: 1px solid #efefef;
  background: #ffffff;
}

.filter-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.filter-group label {
  font-size: 13px;
  font-weight: 600;
  color: #444444;
}

.filter-control {
  width: 100%;
}

:deep(.p-inputtext) {
  width: 100%;
  border-radius: 16px;
  border-color: #dddddd;
  box-shadow: none;
}

:deep(.p-inputtext:enabled:focus) {
  border-color: #111111;
  box-shadow: 0 0 0 3px rgba(196, 18, 23, 0.08);
}

.state-block {
  margin: 24px;
  padding: 28px;
  text-align: center;
  color: #777777;
  border: 1px dashed #d7d7d7;
  border-radius: 18px;
  background: #fafafa;
}

:deep(.back-btn.p-button) {
  background: #ffffff;
  border: 1px solid #d8d8d8;
  color: #111111;
  border-radius: 999px;
  box-shadow: none;
}

:deep(.back-btn.p-button:hover) {
  background: #f7f7f7;
  border-color: #111111;
  color: #111111;
}

:deep(.back-btn.p-button .p-button-icon) {
  color: #c41217;
}

:deep(.refresh-btn.p-button) {
  background: #111111;
  border: 1px solid #111111;
  color: #ffffff;
  border-radius: 999px;
  box-shadow: none;
}

:deep(.refresh-btn.p-button:hover) {
  background: #2a2a2a;
  border-color: #2a2a2a;
}

@media (max-width: 760px) {
  .page-shell {
    padding: 24px 16px 40px;
  }

  .top-bar {
    flex-direction: column;
    align-items: stretch;
  }

  .page-header h1 {
    font-size: 30px;
  }
}

.top-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-bottom: 24px;
}

.top-bar-right {
  display: flex;
  gap: 12px;
  align-items: center;
}

.show-deleted-toggle {
  display: flex;
  align-items: center;
}

:deep(.toggle-btn.p-button) {
  background: #ffffff;
  border: 1px solid #d8d8d8;
  color: #111111;
  border-radius: 999px;
  box-shadow: none;
}

:deep(.toggle-btn.p-button.p-highlight) {
  background: #c41217;
  border-color: #c41217;
  color: #ffffff;
}

@media (max-width: 760px) {
  .top-bar {
    flex-direction: column;
  }

  .top-bar-right {
    width: 100%;
    flex-direction: column;
  }

  .show-deleted-toggle {
    width: 100%;
  }

  :deep(.toggle-btn) {
    width: 100%;
  }
}
</style>
